import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import api from '../services/api';

// Types
interface SipNumber {
  _id: string;
  name: string;
  number: string;
  extension: string;
  locationId: string;
  queueIds?: string[];
  status: string;
  host: string;
  port: number;
  username: string;
  password: string;
  department?: string;
  lastUsed?: string;
  callCount: number;
  totalCallDuration: number;
  isOnline: boolean;
  lastHeartbeat: string;
  configuration: any;
  createdAt: string;
  updatedAt: string;
}

interface SipNumberState {
  sipNumbers: SipNumber[];
  currentSipNumber: SipNumber | null;
  loading: boolean;
  error: string | null;
}

interface SipNumberAction {
  type: string;
  payload?: any;
}

interface SipNumberContextType {
  sipNumbers: SipNumber[];
  currentSipNumber: SipNumber | null;
  loading: boolean;
  error: string | null;
  fetchSipNumbers: () => Promise<void>;
  fetchSipNumber: (id: string) => Promise<SipNumber | null>;
  createSipNumber: (sipNumberData: Partial<SipNumber>) => Promise<SipNumber | null>;
  updateSipNumber: (id: string, sipNumberData: Partial<SipNumber>) => Promise<SipNumber | null>;
  deleteSipNumber: (id: string) => Promise<boolean>;
  updateSipNumberStatus: (id: string, isOnline: boolean) => Promise<boolean>;
  getSipNumberStatistics: (id: string, startDate?: string, endDate?: string) => Promise<any>;
  setCurrentSipNumber: (sipNumber: SipNumber) => void;
  clearError: () => void;
}

// SIP Number Context
const SipNumberContext = createContext<SipNumberContextType | undefined>(undefined);

// SIP Number Reducer
const sipNumberReducer = (state: SipNumberState, action: SipNumberAction): SipNumberState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_SIP_NUMBERS':
      return { ...state, sipNumbers: action.payload, loading: false };
    case 'SET_CURRENT_SIP_NUMBER':
      return { ...state, currentSipNumber: action.payload };
    case 'ADD_SIP_NUMBER':
      return { ...state, sipNumbers: [...state.sipNumbers, action.payload] };
    case 'UPDATE_SIP_NUMBER':
      return {
        ...state,
        sipNumbers: state.sipNumbers.map((sip: SipNumber) =>
          sip._id === action.payload._id ? action.payload : sip
        )
      };
    case 'DELETE_SIP_NUMBER':
      return {
        ...state,
        sipNumbers: state.sipNumbers.filter((sip: SipNumber) => sip._id !== action.payload)
      };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

// Initial State
const initialState = {
  sipNumbers: [],
  currentSipNumber: null,
  loading: false,
  error: null
};

// SIP Number Provider
interface SipNumberProviderProps {
  children: ReactNode;
}

export const SipNumberProvider = ({ children }: SipNumberProviderProps) => {
  const [state, dispatch] = useReducer(sipNumberReducer, initialState);



  // Set loading
  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };

  // Set error
  const setError = (error: string) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Fetch all SIP numbers
  const fetchSipNumbers = async (filters = {}) => {
    try {
      setLoading(true);
      clearError();

      const params = new URLSearchParams(filters);
      
      const response = await api.get(`/sip-numbers?${params}`);

      if (response.data.success) {
        dispatch({ type: 'SET_SIP_NUMBERS', payload: response.data.data });
      } else {
        setError(response.data.message || 'SIP numaraları getirilemedi');
      }
    } catch (error) {
      console.error('SIP numaraları getirme hatası:', error);
      setError((error as any).response?.data?.message || 'SIP numaraları getirilemedi');
    }
  };

  // Fetch single SIP number
  const fetchSipNumber = async (id: string) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.get(`/sip-numbers/${id}`);

      if (response.data.success) {
        dispatch({ type: 'SET_CURRENT_SIP_NUMBER', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'SIP numarası getirilemedi');
        return null;
      }
    } catch (error) {
      console.error('SIP numarası getirme hatası:', error);
      setError((error as any).response?.data?.message || 'SIP numarası getirilemedi');
      return null;
    }
  };

  // Create SIP number
  const createSipNumber = async (sipNumberData: Partial<SipNumber>) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.post(`/sip-numbers`, sipNumberData);

      if (response.data.success) {
        dispatch({ type: 'ADD_SIP_NUMBER', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'SIP numarası oluşturulamadı');
        return null;
      }
    } catch (error) {
      console.error('SIP numarası oluşturma hatası:', error);
      setError((error as any).response?.data?.message || 'SIP numarası oluşturulamadı');
      return null;
    }
  };

  // Update SIP number
  const updateSipNumber = async (id: string, sipNumberData: Partial<SipNumber>) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.put(`/sip-numbers/${id}`, sipNumberData);

      if (response.data.success) {
        dispatch({ type: 'UPDATE_SIP_NUMBER', payload: response.data.data });
        return response.data.data;
      } else {
        setError(response.data.message || 'SIP numarası güncellenemedi');
        return null;
      }
    } catch (error) {
      console.error('SIP numarası güncelleme hatası:', error);
      setError((error as any).response?.data?.message || 'SIP numarası güncellenemedi');
      return null;
    }
  };

  // Delete SIP number
  const deleteSipNumber = async (id: string) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.delete(`/sip-numbers/${id}`);

      if (response.data.success) {
        dispatch({ type: 'DELETE_SIP_NUMBER', payload: id });
        return true;
      } else {
        setError(response.data.message || 'SIP numarası silinemedi');
        return false;
      }
    } catch (error) {
      console.error('SIP numarası silme hatası:', error);
      setError((error as any).response?.data?.message || 'SIP numarası silinemedi');
      return false;
    }
  };

  // Update SIP number status
  const updateSipNumberStatus = async (id: string, isOnline: boolean) => {
    try {
      setLoading(true);
      clearError();

      const response = await api.post(`/sip-numbers/${id}/status`, {
        isOnline
      });

      if (response.data.success) {
        // Update the SIP number in state
        const updatedSipNumber = state.sipNumbers.find((sip: SipNumber) => sip._id === id);
        if (updatedSipNumber) {
          updatedSipNumber.isOnline = isOnline;
          dispatch({ type: 'UPDATE_SIP_NUMBER', payload: updatedSipNumber });
        }
        return true;
      } else {
        setError(response.data.message || 'SIP durumu güncellenemedi');
        return false;
      }
    } catch (error) {
      console.error('SIP durumu güncelleme hatası:', error);
      setError((error as any).response?.data?.message || 'SIP durumu güncellenemedi');
      return false;
    }
  };

  // Get SIP number statistics
  const getSipNumberStatistics = async (id: string, startDate?: string, endDate?: string) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      const response = await api.get(`/sip-numbers/${id}/stats?${params}`);

      if (response.data.success) {
        return response.data;
      } else {
        setError(response.data.message || 'İstatistikler getirilemedi');
        return null;
      }
    } catch (error) {
      console.error('İstatistik getirme hatası:', error);
      setError((error as any).response?.data?.message || 'İstatistikler getirilemedi');
      return null;
    }
  };

  // Set current SIP number
  const setCurrentSipNumber = (sipNumber: SipNumber) => {
    dispatch({ type: 'SET_CURRENT_SIP_NUMBER', payload: sipNumber });
  };

  // Context value
  const value = {
    ...state,
    fetchSipNumbers,
    fetchSipNumber,
    createSipNumber,
    updateSipNumber,
    deleteSipNumber,
    updateSipNumberStatus,
    getSipNumberStatistics,
    setCurrentSipNumber,
    setLoading,
    setError,
    clearError
  };

  return (
    <SipNumberContext.Provider value={value}>
      {children}
    </SipNumberContext.Provider>
  );
};

// SIP Number Hook
export const useSipNumber = (): SipNumberContextType => {
  const context = useContext(SipNumberContext);
  if (!context) {
    throw new Error('useSipNumber must be used within a SipNumberProvider');
  }
  return context;
};

export default SipNumberContext;
